/**
 * `SponsorshipDto` / `SponsorshipBindingModel`, from
 * `Gopanza.Api.Services.Abstractions`.
 *
 * The record itself is four fields. Everything a sponsorship *does* lives in
 * two join tables — `SponsoredItems` and `SponsorshipBusinesses` — and in the
 * moment somebody presses Apply.
 */

export interface Sponsorship {
  id: string;
  name: string | null;
  startDate: string;
  endDate: string;
  createdAt: string;
}

/** What `POST marketing/sponsorships` accepts. An `id` updates, none creates. */
export interface SponsorshipInput {
  id: string | null;
  name: string;
  startDate: string | null;
  endDate: string | null;
}

/** One row of `sponsorship/items/{id}` — an `ItemDto`, trimmed to what shows. */
export interface SponsoredItem {
  id: string;
  gtin: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  itemImage: string | null;
  itemThumbnail: string | null;
}

/** One row of `sponsorship/businesses/{id}` — a `BusinessDto`, trimmed. */
export interface SponsoredBusiness {
  id: string;
  name: string;
}

export type SponsorshipStatus = 'running' | 'scheduled' | 'finished';

/**
 * Where the sponsorship's own window sits relative to now.
 *
 * Note this is *not* whether the sponsorship has been applied. The window is a
 * plan; the catalogue only carries it once somebody presses Apply, and nothing
 * on the record says whether that has happened. See `sponsorships.api`.
 */
export function statusOf(
  sponsorship: Sponsorship,
  now = Date.now()
): SponsorshipStatus {
  const start = Date.parse(`${sponsorship.startDate}Z`);
  const end = Date.parse(`${sponsorship.endDate}Z`);

  if (!Number.isNaN(end) && now > end) return 'finished';
  if (!Number.isNaN(start) && now < start) return 'scheduled';
  return 'running';
}

export function emptySponsorship(): SponsorshipInput {
  return { id: null, name: '', startDate: null, endDate: null };
}
